import React, { useState, useEffect, useRef } from 'react';

export const AudioPlayer = ({ 
  voiceUrl = null,
  musicUrl = null,
  musicVolume = 0.15,
  voiceVolume = 1.0,
  onComplete = () => {},
  autoPlay = false
}) => {
  const [playingVoice, setPlayingVoice] = useState(false);
  const [playingMusic, setPlayingMusic] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);

  const voiceAudioRef = useRef(null);
  const musicAudioRef = useRef(null);
  const animationFrameRef = useRef(null);

  // Detect if mobile
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  useEffect(() => {
    if (voiceUrl && voiceAudioRef.current) {
      console.log('Loading voice URL:', voiceUrl);
      voiceAudioRef.current.src = voiceUrl;
      voiceAudioRef.current.volume = voiceVolume;
      voiceAudioRef.current.playbackRate = playbackSpeed;
      
      voiceAudioRef.current.onloadedmetadata = () => {
        console.log('Voice metadata loaded, duration:', voiceAudioRef.current.duration);
        setDuration(voiceAudioRef.current.duration);
      };

      voiceAudioRef.current.onerror = (e) => {
        console.error('Voice audio error:', e);
        console.error('Audio error code:', voiceAudioRef.current.error?.code);
        console.error('Audio error message:', voiceAudioRef.current.error?.message);
      };

      voiceAudioRef.current.onended = () => {
        console.log('Voice playback ended');
        setPlayingVoice(false);
        onComplete();
      };

      voiceAudioRef.current.ontimeupdate = () => {
        if (playingVoice && voiceAudioRef.current) {
          setCurrentTime(voiceAudioRef.current.currentTime);
        }
      };
    }

    if (musicUrl && musicAudioRef.current) {
      console.log('Loading music URL:', musicUrl);
      musicAudioRef.current.src = musicUrl;
      musicAudioRef.current.volume = musicVolume;
      musicAudioRef.current.loop = true;
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceUrl, musicUrl]);

useEffect(() => {
  console.log('musicVolume prop changed to:', musicVolume);
  if (musicAudioRef.current) {
    musicAudioRef.current.volume = Math.max(0, Math.min(1, musicVolume));
    if (musicVolume <= 0) {
      musicAudioRef.current.muted = true;
    } else {
      musicAudioRef.current.muted = false;
    }
    console.log('Audio element volume set to:', musicAudioRef.current.volume);
  }
}, [musicVolume]);

  useEffect(() => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.volume = voiceVolume;
    }
  }, [voiceVolume]);

  useEffect(() => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  const updateProgress = () => {
    if (voiceAudioRef.current && playingVoice) {
      setCurrentTime(voiceAudioRef.current.currentTime);
    }
    
    if (playingVoice || playingMusic) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    }
  };

  const handlePlayVoice = async () => {
    try {
      console.log('Attempting to play voice...');
      if (voiceUrl && voiceAudioRef.current) {
        const playPromise = voiceAudioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log('Voice playing successfully');
        }
        setPlayingVoice(true);
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    } catch (err) {
      console.error('Error playing voice:', err);
      alert(`Voice playback error: ${err.message}`);
    }
  };

  const handlePauseVoice = () => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
    }
    setPlayingVoice(false);
  };

  const handlePlayMusic = async () => {
    try {
      console.log('Attempting to play music...');
      if (musicUrl && musicAudioRef.current) {
        const playPromise = musicAudioRef.current.play();
        if (playPromise !== undefined) {
          await playPromise;
          console.log('Music playing successfully');
        }
        setPlayingMusic(true);
        animationFrameRef.current = requestAnimationFrame(updateProgress);
      }
    } catch (err) {
      console.error('Error playing music:', err);
    }
  };

  const handlePauseMusic = () => {
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
    }
    setPlayingMusic(false);
  };

  const handlePlayBoth = async () => {
    try {
      console.log('Attempting to play both...');

      if (musicUrl && musicAudioRef.current) {
        const musicPromise = musicAudioRef.current.play();
        if (musicPromise !== undefined) {
          await musicPromise;
        }
        setPlayingMusic(true);
      }
      
      if (voiceUrl && voiceAudioRef.current) {
        const voicePromise = voiceAudioRef.current.play();
        if (voicePromise !== undefined) {
          await voicePromise;
        }
        setPlayingVoice(true);
      }

      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } catch (err) {
      console.error('Error playing audio:', err);
      alert(`Playback error: ${err.message}`);
    }
  };

  const handleStopAll = () => {
    if (voiceAudioRef.current) {
      voiceAudioRef.current.pause();
      voiceAudioRef.current.currentTime = 0;
    }
    if (musicAudioRef.current) {
      musicAudioRef.current.pause();
      musicAudioRef.current.currentTime = 0;
    }
    
    setPlayingVoice(false);
    setPlayingMusic(false);
    setCurrentTime(0);
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
  };

  const handleSeek = (e) => {
    const progressBar = e.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = x / rect.width;
    const newTime = percentage * duration;

    if (voiceAudioRef.current) {
      voiceAudioRef.current.currentTime = newTime;
    }
    setCurrentTime(newTime);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const speedOptions = [0.75, 1.0, 1.25, 1.5];

  if (!voiceUrl && !musicUrl) {
    return null;
  }

  return (
    <div style={styles.container}>
      <audio 
        ref={voiceAudioRef} 
        playsInline 
        preload="metadata"
        crossOrigin="anonymous"
      />
      <audio 
        ref={musicAudioRef} 
        playsInline 
        preload="metadata"
      />

      <h3 style={styles.title}>Audio Playback</h3>

      {/* Compact Control Buttons - Horizontal on Desktop, Stacked on Mobile */}
      <div style={styles.buttonGrid}>
        {musicUrl && (
          <button 
            onClick={playingMusic && !playingVoice ? handlePauseMusic : handlePlayMusic}
            style={{
              ...styles.optionButton,
              ...(playingMusic && !playingVoice ? styles.optionButtonActive : {})
            }}
          >
            <span style={styles.buttonIcon}>🎵</span>
            <span style={styles.buttonText}>
              {playingMusic && !playingVoice ? 'Pause Music' : 'Music Only'}
            </span>
          </button>
        )}

        {voiceUrl && (
          <button 
            onClick={playingVoice && !playingMusic ? handlePauseVoice : handlePlayVoice}
            style={{
              ...styles.optionButton,
              ...(playingVoice && !playingMusic ? styles.optionButtonActive : {})
            }}
          >
            <span style={styles.buttonIcon}>🎤</span>
            <span style={styles.buttonText}>
              {playingVoice && !playingMusic ? 'Pause Voice' : 'Voice Only'}
            </span>
          </button>
        )}

        {voiceUrl && musicUrl && (
          <button 
            onClick={playingVoice && playingMusic ? handleStopAll : handlePlayBoth}
            style={{
              ...styles.optionButton,
              ...(playingVoice && playingMusic ? styles.optionButtonActive : {})
            }}
          >
            <span style={styles.buttonIcon}>🎧</span>
            <span style={styles.buttonText}>
              {playingVoice && playingMusic ? 'Pause Both' : 'Voice + Music'}
            </span>
          </button>
        )}

        {(playingVoice || playingMusic) && (
          <button onClick={handleStopAll} style={styles.stopButton}>
            <span style={styles.buttonIcon}>⏹</span>
            <span style={styles.buttonText}>Stop All</span>
          </button>
        )}
      </div>

      {/* Progress Bar - Only show if voice is playing */}
      {voiceUrl && playingVoice && (
        <div style={styles.progressSection}>
          <div onClick={handleSeek} style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%`
              }}
            />
          </div>
          
          <div style={styles.timeDisplay}>
            <span style={styles.time}>{formatTime(currentTime)}</span>
            <span style={styles.time}>{formatTime(duration)}</span>
          </div>
        </div>
      )}

      {/* Speed Controls - Only for voice */}
      {voiceUrl && playingVoice && (
        <div style={styles.speedControls}>
          <span style={styles.speedLabel}>Playback Speed:</span>
          <div style={styles.speedButtons}>
            {speedOptions.map(speed => (
              <button
                key={speed}
                onClick={() => setPlaybackSpeed(speed)}
                style={{
                  ...styles.speedButton,
                  ...(playbackSpeed === speed ? styles.speedButtonActive : {})
                }}
              >
                {speed}x
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: '#1a1f2e',
    border: '1px solid #2a3142',
    borderRadius: '12px',
    padding: '24px',
  },
  title: {
    color: '#e2e8f0',
    fontSize: '1.125rem',
    fontWeight: '600',
    marginBottom: '16px',
    marginTop: 0,
  },
  buttonGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '10px',
    marginBottom: '20px',
  },
  optionButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    padding: '0.75em 1em',
    backgroundColor: '#2a3142',
    border: '2px solid #2a3142',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#cbd5e1',
    flex: '1 1 auto',
    minWidth: '150px',
  },
  optionButtonActive: {
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    border: '2px solid #14b8a6',
    color: 'white',
  },
  buttonIcon: {
    fontSize: '1.25rem',
    lineHeight: '1',
  },
  buttonText: {
    fontSize: '0.875rem',
    fontWeight: '600',
  },
  stopButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: '12px',
    padding: '0.8571em 1.1429em',
    backgroundColor: '#dc2626',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    flex: '1 1 auto',
    minWidth: '150px',
  },
  progressSection: {
    marginBottom: '16px',
    marginTop: '20px',
  },
  progressBar: {
    width: '100%',
    height: '8px',
    backgroundColor: '#2a3142',
    borderRadius: '4px',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    borderRadius: '4px',
    transition: 'width 0.1s',
  },
  timeDisplay: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: '8px',
  },
  time: {
    fontSize: '0.75rem',
    color: '#cbd5e1',
  },
  speedControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #2a3142',
  },
  speedLabel: {
    fontSize: '0.875rem',
    color: '#cbd5e1',
  },
  speedButtons: {
    display: 'flex',
    gap: '6px',
  },
  speedButton: {
    padding: '0.6154em 1.0769em',
    fontSize: '0.8125rem',
    backgroundColor: '#2a3142',
    color: '#cbd5e1',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  speedButtonActive: {
    background: 'linear-gradient(135deg, #14b8a6 0%, #0ea5e9 100%)',
    color: 'white',
  },
};